import { useEffect, useState } from "react";
import { HelpCircle, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

export const HubQuestionCard = ({ slug, leadId, seconds, accent = "#EC4899", light = false }) => {
  const [queue, setQueue] = useState([]);
  const [active, setActive] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!leadId) return;
    api.get(`/public/campaigns/${slug}/questions`, { params: { lead_id: leadId } })
      .then((r) => setQueue([...r.data.questions].sort((a, b) => a.after_seconds - b.after_seconds))).catch(() => {});
  }, [slug, leadId]);

  useEffect(() => {
    if (active || !queue.length || seconds < queue[0].after_seconds) return;
    setActive(queue[0]);
    setAnswer("");
  }, [seconds, queue, active]);

  if (!active) return null;
  const submit = async (skipped) => {
    setBusy(true);
    try { await api.post(`/public/campaigns/${slug}/questions/answer`, { lead_id: leadId, question_id: active.id, answer: skipped ? "" : answer, skipped }); } catch { /* keep the viewer moving */ }
    setQueue((q) => q.filter((x) => x.id !== active.id));
    setActive(null);
    setBusy(false);
  };
  const box = light ? "bg-white border-slate-200 text-slate-900" : "bg-[#141A24] border-white/10 text-white";
  const dim = light ? "text-slate-500" : "text-slate-400";

  return (
    <div data-testid="hub-question-card" className={`fixed bottom-5 left-5 z-40 w-[min(340px,calc(100vw-2.5rem))] rounded-2xl border p-5 shadow-2xl sv-rise ${box}`}>
      <button data-testid="hub-question-close" onClick={() => submit(true)} aria-label="Skip question" className={`absolute top-3 right-3 ${dim} hover:opacity-70`}><X className="w-4 h-4" /></button>
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5" style={{ color: accent }}><HelpCircle className="w-3.5 h-3.5" /> Quick question</p>
      <p className="font-semibold text-sm mb-3 pr-4" data-testid="hub-question-text">{active.question}</p>
      <select data-testid="hub-question-select" value={answer} onChange={(e) => setAnswer(e.target.value)}
        className={`w-full h-10 px-3 rounded-lg border text-sm focus:outline-none ${light ? "bg-slate-50 border-slate-200" : "bg-black/30 border-white/10"}`}>
        <option value="">Choose an answer…</option>
        {active.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <div className="flex justify-end gap-2 mt-4">
        <button data-testid="hub-question-skip" disabled={busy} onClick={() => submit(true)} className={`h-9 px-4 rounded-full text-sm ${dim} hover:opacity-80`}>Skip</button>
        <button data-testid="hub-question-submit" disabled={busy || !answer} onClick={() => submit(false)}
          className="h-9 px-5 rounded-full text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2" style={{ background: accent }}>
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Submit
        </button>
      </div>
    </div>
  );
};

const inputCls = "w-full h-10 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none";
const newQ = () => ({ id: `q${Date.now().toString(36)}`, question: "", options: ["", ""], after_seconds: 60 });

const QuestionRow = ({ q, i, set, remove }) => (
  <div className="rounded-lg border border-[#242C3D] bg-[#0B0F17]/50 p-4 space-y-3" data-testid={`hub-q-editor-${i}`}>
    <div className="flex gap-3 items-start">
      <span className="font-mono text-xs text-indigo-300 pt-3">Q{i + 1}</span>
      <input data-testid={`hub-q-text-${i}`} required value={q.question} maxLength={200} onChange={(e) => set({ question: e.target.value })} placeholder="What best describes your role?" className={inputCls} />
      <div className="shrink-0 w-36">
        <div className="flex items-center gap-2">
          <input data-testid={`hub-q-seconds-${i}`} type="number" min={5} max={7200} required value={q.after_seconds} onChange={(e) => set({ after_seconds: Number(e.target.value) })} className={`${inputCls} font-mono`} />
          <span className="text-xs text-slate-500">sec</span>
        </div>
      </div>
      <button type="button" data-testid={`hub-q-remove-${i}`} onClick={remove} className="h-10 w-10 shrink-0 grid place-items-center rounded-md text-slate-500 hover:text-rose-400 hover:bg-white/5"><X className="w-4 h-4" /></button>
    </div>
    <div className="pl-8">
      <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Dropdown options (one per line, 2-10)</p>
      <textarea data-testid={`hub-q-options-${i}`} rows={3} value={q.options.join("\n")}
        onChange={(e) => set({ options: e.target.value.split("\n").slice(0, 10) })}
        placeholder={"IT leader\nSecurity engineer\nOther"} className={`${inputCls} h-auto py-2 resize-y text-xs`} />
    </div>
  </div>
);

export const HubQuestionsEditor = ({ questions, onChange }) => {
  const set = (i, p) => onChange(questions.map((q, j) => (j === i ? { ...q, ...p } : q)));
  return (
    <section className="rounded-lg border border-[#242C3D] bg-[#141A24]/70 p-6 space-y-4" data-testid="hub-questions-editor">
      <div>
        <h2 className="font-display font-semibold text-lg">Engagement questions</h2>
        <p className="text-xs text-slate-500 mt-1">Up to 3 dropdown questions shown while visitors watch or read any content in this hub, after the number of seconds you set. Each visitor answers each question once; answers appear in leads, CSV exports and Reports.</p>
      </div>
      {questions.map((q, i) => <QuestionRow key={q.id} q={q} i={i} set={(p) => set(i, p)} remove={() => onChange(questions.filter((_, j) => j !== i))} />)}
      {questions.length === 0 && <p className="text-sm text-slate-500" data-testid="hub-questions-empty">No questions. Visitors won't be asked anything.</p>}
      {questions.length < 3 && (
        <button type="button" data-testid="hub-q-add" onClick={() => onChange([...questions, newQ()])} className="h-9 px-4 rounded-full border border-dashed border-[#242C3D] text-sm text-slate-300 hover:border-indigo-500 hover:text-white">+ Add question ({questions.length}/3)</button>
      )}
    </section>
  );
};
